/**
 * Microsoft Advertising — Row Transformers (Story 19.2).
 *
 * Pure functions that convert raw `Record<string, string>` rows from
 * `parseMicrosoftAdsCsv()` (PascalCase keys, all string values) into typed
 * camelCase records aligned with `MICROSOFT_ADS_METRICS[].key`.
 *
 * Key conventions:
 *   - Numeric helpers return `0` for `--`/empty/non-finite (consistent with
 *     google-ads.ts `parseMetric`).
 *   - Percentage helpers normalise from Microsoft's display percent (e.g. 2.34
 *     for 2.34%) to fraction-of-1 (0.0234) — the codebase invariant.
 *   - `parseQualityScoreEnum` returns `null` (not 0) for unknown buckets so
 *     widgets can show "no data" rather than a misleading 0/10.
 *
 * Every transformer adds a `_reportType` discriminator so widgets can filter
 * `records.filter(r => r._reportType === "campaigns")`.
 */

import { MICROSOFT_ADS_DEPRECATED_COLUMNS } from "./report-builders";
import type { MicrosoftAdsReportType } from "./reporting";

// ============================================================================
// Primitive parsers
// ============================================================================

/**
 * Parse a numeric value out of a Microsoft-Ads CSV cell. Microsoft uses `--`
 * to mean "no data" and may pad with thousands separators on some locales.
 * Returns `0` for any non-numeric / non-finite input. Mirrors the
 * `0`-fallback semantics of the Google Ads adapter for consistency.
 */
export function parseMetric(v: string | undefined | null): number {
  if (v == null) return 0;
  const s = String(v).trim();
  if (s === "" || s === "--") return 0;
  // Strip thousands separators only when the comma sits between digits
  // (preserves a future locale that uses comma as decimal separator).
  const cleaned = s.replace(/,(?=\d{3}(?:\D|$))/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Parse Microsoft's display-percent (e.g. `2.34` for 2.34%) into the
 * codebase's canonical fraction-of-1 (`0.0234`). `--` / empty / non-finite
 * → `0`. Microsoft's CSV reports CTR, ConversionRate, and every
 * `*Percent` field as a display percent; widgets multiply back by 100 for UI.
 */
export function parsePercent(v: string | undefined | null): number {
  const n = parseMetric(v);
  return n / 100;
}

/**
 * Parse Microsoft's Quality-Score-bucket value into a 1-3 number, the 1-10
 * QS into a number, or `null` for unknown / no-data buckets.
 *
 * Microsoft returns:
 *   - QualityScore: integer 1..10 or "--"
 *   - ExpectedCtr / AdRelevance / LandingPageExperience: "1" (below average) /
 *     "2" (average) / "3" (above average) — string-typed
 *   - Any other value (e.g. a future "MIXED" bucket): we return null so
 *     widgets show "no data" instead of a false 0/10.
 */
export function parseQualityScoreEnum(
  v: string | undefined | null
): number | null {
  if (v == null) return null;
  const s = String(v).trim();
  if (s === "" || s === "--") return null;
  // Whitelist: only accept numeric strings 1..10 (covers both 1-3 buckets and
  // the 1-10 QS scale).
  const n = Number(s);
  if (!Number.isInteger(n)) return null;
  if (n < 1 || n > 10) return null;
  return n;
}

/**
 * Strip query strings, fragments, and trailing slashes from a Final URL.
 *
 * Story 19.3 Round 4 (Red Team Attack 5 — PII leakage via finalUrl): URL
 * query strings in real customer Microsoft Ads accounts can carry PII (email
 * addresses, session IDs, UTM tags). Reports may surface in shared portals,
 * so we sanitize before persistence. Also collapses `http://X` and
 * `https://X` variants to one canonical form (Pre-mortem F6).
 *
 * Pure function — no allocations beyond the result string. Called from any
 * transformer that surfaces `finalUrl` (none today; Story 19.3 fixture-mode
 * uses it; future live-mode columns will too).
 *
 * Example: `https://cloudstack.io/pricing?utm=email&id=user@example.com#section1`
 *      →   `https://cloudstack.io/pricing`
 */
export function normalizeFinalUrl(v: string | undefined | null): string {
  if (v == null) return "";
  const s = String(v).trim();
  if (s === "" || s === "--") return "";
  // Strip fragment first (so query separator detection works), then query.
  let cleaned = s.split("#")[0] ?? "";
  cleaned = cleaned.split("?")[0] ?? "";
  // Drop trailing slash (collapses /pricing/ and /pricing into one row).
  if (cleaned.length > 1 && cleaned.endsWith("/")) {
    cleaned = cleaned.slice(0, -1);
  }
  return cleaned;
}

/**
 * Parse Microsoft's `HH:MM:SS` duration into total seconds. Returns `0` for
 * empty / `--` / unparseable. Used by `Duration` (call extension) and
 * `VideoDuration`.
 */
export function parseDuration(v: string | undefined | null): number {
  if (v == null) return 0;
  const s = String(v).trim();
  if (s === "" || s === "--") return 0;
  const parts = s.split(":");
  if (parts.length !== 3) return 0;
  const [h, m, sec] = parts.map((p) => Number(p));
  if (!Number.isFinite(h) || !Number.isFinite(m) || !Number.isFinite(sec)) return 0;
  return (h ?? 0) * 3600 + (m ?? 0) * 60 + (sec ?? 0);
}

// ============================================================================
// Deprecated-column runtime guard
// ============================================================================

/**
 * Throws if any deprecated column appears in the parsed CSV header keys.
 * Microsoft removed these in 2021-2022; if they ever return them as default
 * fields, this guard surfaces a hard failure that the test suite catches.
 */
export function assertNoDeprecatedRows(
  rows: ReadonlyArray<Record<string, string>>,
  reportType: string
): void {
  if (rows.length === 0) return;
  const sample = rows[0];
  if (!sample) return;
  for (const key of Object.keys(sample)) {
    if (
      (MICROSOFT_ADS_DEPRECATED_COLUMNS as readonly string[]).includes(key)
    ) {
      throw new Error(
        `Microsoft Ads ${reportType}: deprecated column "${key}" present in response — refusing to surface stale 0 values`
      );
    }
  }
}

// ============================================================================
// Common attribute extractors
// ============================================================================

/**
 * Extract delivery + spend metrics shared across every report type.
 */
function extractCoreDelivery(row: Record<string, string>): Record<string, number> {
  return {
    impressions: parseMetric(row.Impressions),
    clicks: parseMetric(row.Clicks),
    ctr: parsePercent(row.Ctr),
    spend: parseMetric(row.Spend),
    averageCpc: parseMetric(row.AverageCpc),
    averageCpm: parseMetric(row.AverageCpm),
  };
}

function extractCoreConversion(row: Record<string, string>): Record<string, number> {
  return {
    conversionsQualified: parseMetric(row.ConversionsQualified),
    allConversionsQualified: parseMetric(row.AllConversionsQualified),
    conversionRate: parsePercent(row.ConversionRate),
    allConversionRate: parsePercent(row.AllConversionRate),
    costPerConversion: parseMetric(row.CostPerConversion),
    allCostPerConversion: parseMetric(row.AllCostPerConversion),
    viewThroughConversions: parseMetric(row.ViewThroughConversions),
    viewThroughConversionRate: parsePercent(row.ViewThroughConversionRate),
    viewThroughCostPerConversion: parseMetric(row.ViewThroughCostPerConversion),
  };
}

function extractCoreRevenue(row: Record<string, string>): Record<string, number> {
  return {
    revenue: parseMetric(row.Revenue),
    allRevenue: parseMetric(row.AllRevenue),
    returnOnAdSpend: parseMetric(row.ReturnOnAdSpend),
    allReturnOnAdSpend: parseMetric(row.AllReturnOnAdSpend),
    revenuePerConversion: parseMetric(row.RevenuePerConversion),
    allRevenuePerConversion: parseMetric(row.AllRevenuePerConversion),
  };
}

function extractImpressionShare(row: Record<string, string>): Record<string, number> {
  return {
    impressionSharePercent: parsePercent(row.ImpressionSharePercent),
    impressionShareLostToBudgetPercent: parsePercent(row.ImpressionShareLostToBudgetPercent),
    impressionShareLostToRankPercent: parsePercent(row.ImpressionShareLostToRankPercent),
    clickSharePercent: parsePercent(row.ClickSharePercent),
    absoluteTopImpressionRatePercent: parsePercent(row.AbsoluteTopImpressionRatePercent),
    absoluteTopImpressionSharePercent: parsePercent(row.AbsoluteTopImpressionSharePercent),
    topImpressionRatePercent: parsePercent(row.TopImpressionRatePercent),
    topImpressionSharePercent: parsePercent(row.TopImpressionSharePercent),
    audienceImpressionSharePercent: parsePercent(row.AudienceImpressionSharePercent),
  };
}

// ============================================================================
// Per-report-type transformers
// ============================================================================

export interface OverviewRecord {
  _reportType: "overview";
  date: string;
  accountId: string;
  accountName: string;
  accountNumber: string;
  currencyCode: string;
  // Numeric fields may also be `null` if a future iteration adds nullable
  // metrics (e.g. quality scores) — keep the index signature permissive.
  [key: string]: string | number | null;
}

export function transformOverviewRows(
  rows: ReadonlyArray<Record<string, string>>
): OverviewRecord[] {
  assertNoDeprecatedRows(rows, "overview");
  return rows.map((row) => ({
    _reportType: "overview" as const,
    date: row.TimePeriod ?? "",
    accountId: row.AccountId ?? "",
    accountName: row.AccountName ?? "",
    accountNumber: row.AccountNumber ?? "",
    currencyCode: row.CurrencyCode ?? "",
    ...extractCoreDelivery(row),
    ...extractCoreConversion(row),
    ...extractCoreRevenue(row),
  }));
}

export function transformCampaignsRows(
  rows: ReadonlyArray<Record<string, string>>
): Array<Record<string, string | number>> {
  assertNoDeprecatedRows(rows, "campaigns");
  return rows.map((row) => ({
    _reportType: "campaigns",
    date: row.TimePeriod ?? "",
    accountId: row.AccountId ?? "",
    accountName: row.AccountName ?? "",
    campaignId: row.CampaignId ?? "",
    campaignName: row.CampaignName ?? "",
    campaignStatus: row.CampaignStatus ?? "",
    campaignType: row.CampaignType ?? "",
    bidStrategyType: row.BidStrategyType ?? "",
    currencyCode: row.CurrencyCode ?? "",
    ...extractCoreDelivery(row),
    dailySpend: parseMetric(row.DailySpend),
    ...extractCoreConversion(row),
    ...extractCoreRevenue(row),
    ...extractImpressionShare(row),
  }));
}

export function transformAdGroupsRows(
  rows: ReadonlyArray<Record<string, string>>
): Array<Record<string, string | number>> {
  assertNoDeprecatedRows(rows, "adGroups");
  return rows.map((row) => ({
    _reportType: "adGroups",
    date: row.TimePeriod ?? "",
    accountId: row.AccountId ?? "",
    campaignId: row.CampaignId ?? "",
    campaignName: row.CampaignName ?? "",
    adGroupId: row.AdGroupId ?? "",
    adGroupName: row.AdGroupName ?? "",
    adGroupStatus: row.AdGroupStatus ?? "",
    adGroupType: row.AdGroupType ?? "",
    currencyCode: row.CurrencyCode ?? "",
    ...extractCoreDelivery(row),
    ...extractCoreConversion(row),
    ...extractCoreRevenue(row),
  }));
}

export function transformKeywordsRows(
  rows: ReadonlyArray<Record<string, string>>
): Array<Record<string, string | number | null>> {
  assertNoDeprecatedRows(rows, "keywords");
  return rows.map((row) => ({
    _reportType: "keywords",
    date: row.TimePeriod ?? "",
    accountId: row.AccountId ?? "",
    campaignId: row.CampaignId ?? "",
    campaignName: row.CampaignName ?? "",
    adGroupId: row.AdGroupId ?? "",
    adGroupName: row.AdGroupName ?? "",
    keyword: row.Keyword ?? "",
    keywordId: row.KeywordId ?? "",
    keywordStatus: row.KeywordStatus ?? "",
    bidMatchType: row.BidMatchType ?? "",
    deliveredMatchType: row.DeliveredMatchType ?? "",
    currentMaxCpc: parseMetric(row.CurrentMaxCpc),
    currencyCode: row.CurrencyCode ?? "",
    ...extractCoreDelivery(row),
    ...extractCoreConversion(row),
    ...extractCoreRevenue(row),
    qualityScore: parseQualityScoreEnum(row.QualityScore),
    historicalQualityScore: parseQualityScoreEnum(row.HistoricalQualityScore),
    qualityImpact: parseQualityScoreEnum(row.QualityImpact),
    expectedCtr: parseQualityScoreEnum(row.ExpectedCtr),
    historicalExpectedCtr: parseQualityScoreEnum(row.HistoricalExpectedCtr),
    adRelevance: parseQualityScoreEnum(row.AdRelevance),
    historicalAdRelevance: parseQualityScoreEnum(row.HistoricalAdRelevance),
    landingPageExperience: parseQualityScoreEnum(row.LandingPageExperience),
    historicalLandingPageExperience: parseQualityScoreEnum(
      row.HistoricalLandingPageExperience
    ),
  }));
}

export function transformSearchQueriesRows(
  rows: ReadonlyArray<Record<string, string>>
): Array<Record<string, string | number>> {
  assertNoDeprecatedRows(rows, "searchQueries");
  return rows.map((row) => ({
    _reportType: "searchQueries",
    date: row.TimePeriod ?? "",
    accountId: row.AccountId ?? "",
    campaignId: row.CampaignId ?? "",
    campaignName: row.CampaignName ?? "",
    adGroupId: row.AdGroupId ?? "",
    adGroupName: row.AdGroupName ?? "",
    keyword: row.Keyword ?? "",
    searchQuery: row.SearchQuery ?? "",
    deliveredMatchType: row.DeliveredMatchType ?? "",
    currencyCode: row.CurrencyCode ?? "",
    ...extractCoreDelivery(row),
    ...extractCoreConversion(row),
    revenue: parseMetric(row.Revenue),
    allRevenue: parseMetric(row.AllRevenue),
  }));
}

export function transformShoppingRows(
  rows: ReadonlyArray<Record<string, string>>
): Array<Record<string, string | number>> {
  assertNoDeprecatedRows(rows, "shopping");
  return rows.map((row) => ({
    _reportType: "shopping",
    date: row.TimePeriod ?? "",
    accountId: row.AccountId ?? "",
    campaignId: row.CampaignId ?? "",
    campaignName: row.CampaignName ?? "",
    merchantProductId: row.MerchantProductId ?? "",
    productCategory1: row.ProductCategory1 ?? "",
    productCategory2: row.ProductCategory2 ?? "",
    productType1: row.ProductType1 ?? "",
    benchmarkBid: parseMetric(row.BenchmarkBid),
    benchmarkCtr: parsePercent(row.BenchmarkCtr),
    currencyCode: row.CurrencyCode ?? "",
    ...extractCoreDelivery(row),
    ...extractCoreConversion(row),
    revenue: parseMetric(row.Revenue),
    allRevenue: parseMetric(row.AllRevenue),
    returnOnAdSpend: parseMetric(row.ReturnOnAdSpend),
  }));
}

export function transformAgeGenderRows(
  rows: ReadonlyArray<Record<string, string>>
): Array<Record<string, string | number>> {
  assertNoDeprecatedRows(rows, "ageGender");
  return rows.map((row) => ({
    _reportType: "ageGender",
    date: row.TimePeriod ?? "",
    accountId: row.AccountId ?? "",
    campaignId: row.CampaignId ?? "",
    campaignName: row.CampaignName ?? "",
    adGroupId: row.AdGroupId ?? "",
    adGroupName: row.AdGroupName ?? "",
    ageGroup: row.AgeGroup ?? "",
    gender: row.Gender ?? "",
    currencyCode: row.CurrencyCode ?? "",
    ...extractCoreDelivery(row),
    ...extractCoreConversion(row),
  }));
}

export function transformGeoRows(
  rows: ReadonlyArray<Record<string, string>>
): Array<Record<string, string | number>> {
  assertNoDeprecatedRows(rows, "geo");
  return rows.map((row) => ({
    _reportType: "geo",
    date: row.TimePeriod ?? "",
    accountId: row.AccountId ?? "",
    campaignId: row.CampaignId ?? "",
    campaignName: row.CampaignName ?? "",
    country: row.Country ?? "",
    state: row.State ?? "",
    city: row.City ?? "",
    metroArea: row.MetroArea ?? "",
    postalCode: row.PostalCode ?? "",
    locationType: row.LocationType ?? "",
    currencyCode: row.CurrencyCode ?? "",
    ...extractCoreDelivery(row),
    ...extractCoreConversion(row),
  }));
}

export const TRANSFORMERS: Record<
  MicrosoftAdsReportType,
  (rows: ReadonlyArray<Record<string, string>>) => Array<Record<string, unknown>>
> = {
  overview: transformOverviewRows,
  campaigns: transformCampaignsRows,
  adGroups: transformAdGroupsRows,
  keywords: transformKeywordsRows,
  searchQueries: transformSearchQueriesRows,
  shopping: transformShoppingRows,
  ageGender: transformAgeGenderRows,
  geo: transformGeoRows,
};
